import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DeleteTicketValidationSchema,
  type TDeleteTicketValidation,
  useDeleteTicketMutation,
} from '../../../api/delete-ticket.api';
import { useCustomToast } from '@/hooks/use-custom-toast';

import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_SINGLE_TICKET_QUERY_KEY } from '../../../api/get-single-ticket.api';
import { TOAST_MESSAGES_DELETE_TICKET } from './delete-ticket-modal.constant';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

export interface IUseDeleteTicketModalProps {
  spaceId: string;
  ticketId: string;
}

export function useDeleteTicketModal({ spaceId, ticketId }: IUseDeleteTicketModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TDeleteTicketValidation>({
    resolver: zodResolver(DeleteTicketValidationSchema),
    mode: 'onChange',
  });

  const mutationDeleteTicket = useDeleteTicketMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_TICKET.DELETE_TICKET_ERROR;
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: () => {
        if (spaceId && ticketId) {
          Promise.all([
            // queryClient.invalidateQueries({
            //   queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
            // }),
            queryClient.invalidateQueries({
              queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
            }),
          ]);
        }
        modalActions.closeModal('DeleteTicketModal');
        showToast({ message: TOAST_MESSAGES_DELETE_TICKET.DELETE_TICKET_SUCCESS, type: 'success' });
      },
    },
  });

  // HELPERS
  /*
    Separate loading form and loading mutation
   */
  const isOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteTicketModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationDeleteTicket.isPending || form.formState.isSubmitting;
  }, [mutationDeleteTicket, form.formState.isSubmitting]);

  const isLoadingForm = useMemo(() => {
    return mutationDeleteTicket.isPending || form.formState.isSubmitting;
  }, [mutationDeleteTicket, form.formState.isSubmitting]);

  // EFFECTS
  useEffect(() => {
    if (ticketId) {
      form.reset({
        ticketId: ticketId,
      });
    }
  }, [ticketId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('DeleteTicketModal');
  }, [modalActions]);

  const handleCancelModal = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  const handleSubmitForm = useCallback(
    (data: TDeleteTicketValidation) => {
      if (!data?.ticketId) {
        return;
      }
      mutationDeleteTicket.mutate({
        ticketId: data?.ticketId,
      });
    },
    [mutationDeleteTicket]
  );

  return {
    // Form
    form,

    // Modal
    isOpen,

    // Mutation
    mutationDeleteTicket,

    // Helpers
    isDisabledForm,
    isLoadingForm,

    // Handlers
    handleCloseModal,
    handleCancelModal,
    handleSubmitForm,
  };
}
